import agwaAuditChangesStyles from './agwa-audit-changes.styles.css?inline';
import { LitElement, html, nothing, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/format-date/format-date.js';
import type { TemplateResult } from 'lit';
import { convertToMarkdownTemplateResult } from '../../utility/markdown';
import { keptTextMarkdown, renderKeptText } from '../../language/kept-text';
import type { AuditFieldChange } from './audit-entry';

/**
 * The expanded detail of one audit event: what changed, field by field, and any note written about
 * it.
 *
 * WHY THIS IS ITS OWN ELEMENT rather than a template on the trail. `agwa-lit-table` renders the row
 * extension inside ITS shadow root, so a template built by the trail component arrives somewhere the
 * trail's stylesheet cannot reach. The markup was landing entirely unstyled - no column spacing, no
 * alignment, values colliding with the labels beside them. Anything a consumer hands the table has
 * to carry its own styles, which for a custom element it does.
 */
@customElement('agwa-audit-changes')
export class AgwaAuditChangesElement extends LitElement {
  static styles = [unsafeCSS(agwaAuditChangesStyles)];

  /** The field-by-field detail of this event, in the order the server recorded it. */
  @property({ attribute: false }) changes: readonly AuditFieldChange[] = [];

  /** Notes written by a person about this event, already de-duplicated by the trail. */
  @property({ attribute: false }) notes: readonly string[] = [];

  /**
   * The notes above that are server-written kept text, whose tags are resolved; every other note is
   * drawn exactly as stored, its braces as typed (U50). Supplied by the trail from its host's rule.
   */
  @property({ attribute: false }) keptNotes: readonly string[] = [];

  /** The action this detail belongs to, restated so a long detail is never orphaned by scrolling. */
  @property({ type: String }) actionLabel = '';

  /** When the event was saved, restated for the same reason. */
  @property({ type: String }) createdUtc = '';

  /**
   * How a present value is drawn, for a host whose values need more than kept text: the security
   * audit log splits a multi-line value into lines (U47). It is handed whether the host declared the
   * change server-written (`keptText`), so it resolves tags only in those (U50). When unset, a change the host declared
   * server-written (`keptText`) is drawn as kept text (renderKeptText), so a person reference in it
   * (a GDC document's owners) reads as that person's name with their card (CONTACT-010), and any
   * other value is drawn exactly as stored (U50); an absent value always reads as this component's
   * own aside.
   */
  @property({ attribute: false }) valueTemplate?: (
    value: string,
    kept: boolean
  ) => TemplateResult | string;

  /**
   * The column headings only earn their space once there is a list to read. A single change reads as
   * a sentence and a header above it is chrome explaining what is already obvious.
   */
  private get showHeadings(): boolean {
    return this.changes.length > 1;
  }

  private renderChanges(): TemplateResult | typeof nothing {
    if (this.changes.length === 0) return nothing;

    return html`<div class="changes" role="table">
      ${
        this.showHeadings
          ? html`<div class="row heading" role="row">
              <span role="columnheader">Field</span>
              <span role="columnheader">From</span>
              <span aria-hidden="true"></span>
              <span role="columnheader">To</span>
            </div>`
          : nothing
      }
      ${this.changes.map(
        (change) =>
          html`<div class="row" role="row">
            <span class="field" role="cell">${change.label}</span>
            <span class="value from" role="cell">
              ${renderValue(
                change.oldValue,
                // A set's member had no "before" to be unset: it was added (SET-LOOKUP-008).
                change.member ? 'added' : 'not set',
                this.valueTemplate,
                change.keptText === true
              )}
            </span>
            <span class="arrow" aria-hidden="true">&rarr;</span>
            <span class="value to" role="cell">
              ${renderValue(
                change.newValue,
                change.member ? 'removed' : 'cleared',
                this.valueTemplate,
                change.keptText === true
              )}
            </span>
          </div>`
      )}
    </div>`;
  }

  private renderNotes(): TemplateResult | typeof nothing {
    if (this.notes.length === 0) return nothing;

    return html`<div class="notes">
      <span class="notes-label"
        >${this.notes.length > 1 ? 'Notes' : 'Note'}</span
      >
      ${this.notes.map(
        (note) =>
          html`<div class="note">
            ${renderNote(note, this.keptNotes.includes(note))}
          </div>`
      )}
    </div>`;
  }

  /**
   * Names the row this content hangs off. The trail states it too, but only while both are on
   * screen - a twelve-field change is taller than that, and a detail a reader has scrolled into
   * should still say which event it describes.
   */
  private renderAnchor(): TemplateResult | typeof nothing {
    if (!this.actionLabel && !this.createdUtc) return nothing;

    return html`<div class="anchor">
      <span class="anchor-action">${this.actionLabel}</span>
      ${
        this.createdUtc
          ? html`<sl-format-date
              .date=${this.createdUtc}
              month="short"
              day="numeric"
              year="numeric"
              hour="2-digit"
              minute="2-digit"
            ></sl-format-date>`
          : nothing
      }
    </div>`;
  }

  render() {
    return html`<div class="detail">
      ${this.renderAnchor()}${this.renderChanges()}${this.renderNotes()}
    </div>`;
  }
}

/**
 * One side of a change. An absent side is this component speaking about what is not there, so it
 * reads as an aside and never as a value a reader might mistake for stored content.
 */
function renderValue(
  value: string | undefined,
  absentLabel: string,
  template:
    ((value: string, kept: boolean) => TemplateResult | string) | undefined,
  kept: boolean
): TemplateResult | string {
  const text = value?.trim();
  if (!text) return html`<span class="absent">${absentLabel}</span>`;
  if (template) return template(text, kept);
  return kept ? renderKeptText(text) : text;
}

/**
 * Notes are stored as plain text or as markdown behind an `@#MD` marker, a convention that predates
 * this component and is still written by the note-appending jobs. A note the host declared
 * server-written is kept text: a person the server named in it (a review taken over from someone,
 * CONTACT-010) is a reference, drawn as their name with their card, in markdown as much as in plain
 * text. Any other note is a person's own words and is drawn as written, braces and all, with no tag
 * resolved (U50): resolving it would show a MISSING TERM marker for a typed "{{x}}" and let a typed
 * person tag draw as someone else's card.
 */
function renderNote(note: string, kept: boolean): TemplateResult | string {
  const raw = note.trim();
  if (raw.startsWith('@#MD')) {
    const markdown = raw.slice(4).trim();
    if (!markdown) return '';
    // Only kept text may carry a person element through the sanitiser; a typed note's is removed.
    return kept
      ? convertToMarkdownTemplateResult(keptTextMarkdown(markdown), true, {
          keptPeople: true,
        })
      : convertToMarkdownTemplateResult(markdown);
  }
  return kept ? renderKeptText(raw) : raw;
}

declare global {
  interface HTMLElementTagNameMap {
    'agwa-audit-changes': AgwaAuditChangesElement;
  }
}
